import React, { useState } from "react";
import { Button, Input, Space } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { tareasApi } from "../../../services/tareas.api";
import { getMsg } from "../../../utils/antdStatic";

interface AttachmentItem {
  key?: string;
  comentario?: string | null;
  [k: string]: any;
}

interface Props {
  taskId: string | number | null | undefined;
  /** Clave del attachment (imagen/video o documento). */
  attachmentKey?: string;
  comment?: string | null;
  isAdmin: boolean;
  /** Tipo de attachment: define el endpoint y qué lista devuelve el backend. */
  kind?: "imagenes" | "documentos";
  /** Recibe la lista actualizada (con URLs firmadas) tras guardar. */
  onUpdated: (
    updater: AttachmentItem[] | ((prev: AttachmentItem[]) => AttachmentItem[]),
  ) => void;
}

const panelBase: React.CSSProperties = {
  padding: "10px 14px",
  background: "#fafafa",
  borderTop: "1px solid #eee",
  fontSize: 13,
  color: "#444",
};

/**
 * Comentario por foto/video/documento EN LÍNEA en Tareas.
 *
 * Diseño (round 2026-07-01):
 *  - CLICK PARA EDITAR: se hace clic sobre el texto (o el placeholder "Agregar
 *    comentario…") y aparece el textarea enfocado para escribir de una. Sin botón
 *    intermedio ni modal aparte.
 *  - El borrador (`draft`) vive en estado LOCAL de este componente → el tipeo no
 *    re-renderiza al padre (antes el estado vivía en tareas.tsx y laggeaba).
 *  - Genérico por `kind`: sirve para imágenes/videos y documentos.
 */
const InlineComment: React.FC<Props> = ({
  taskId,
  attachmentKey,
  comment,
  isAdmin,
  kind = "imagenes",
  onUpdated,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment ?? "");
  const [saving, setSaving] = useState(false);
  const [hover, setHover] = useState(false);

  const editable = isAdmin && !!attachmentKey;

  const startEdit = () => {
    setDraft(comment ?? "");
    setEditing(true);
  };

  const save = async () => {
    if (!taskId || !attachmentKey) return;
    setSaving(true);
    try {
      const res = await tareasApi.setAttachmentComment(String(taskId), {
        kind,
        key: attachmentKey,
        comentario: draft.trim() || null,
      });
      if (res?.success) {
        // El backend devuelve la lista del `kind` con URLs firmadas frescas.
        const updated = res.data?.[kind];
        if (Array.isArray(updated)) {
          onUpdated(updated as AttachmentItem[]);
        } else {
          // Fallback: reflejar sólo el comentario localmente.
          onUpdated((prev) =>
            prev.map((it) =>
              it.key === attachmentKey
                ? { ...it, comentario: draft.trim() || null }
                : it,
            ),
          );
        }
        setEditing(false);
        getMsg()?.success("Comentario guardado");
      }
    } catch (e: any) {
      getMsg()?.error(e?.message || "No se pudo guardar el comentario");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div style={{ ...panelBase, display: "flex", flexDirection: "column", gap: 8 }}>
        <Input.TextArea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Escribe un comentario (visible para el cliente si la tarea es compartida)"
          maxLength={2000}
          showCount
          autoSize={{ minRows: 2, maxRows: 5 }}
          disabled={saving}
        />
        <Space style={{ alignSelf: "flex-end" }}>
          <Button size="small" onClick={() => setEditing(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button size="small" type="primary" loading={saving} onClick={save}>
            Guardar
          </Button>
        </Space>
      </div>
    );
  }

  return (
    <div style={{ ...panelBase, minHeight: 40 }}>
      <div
        onClick={editable ? startEdit : undefined}
        onMouseEnter={() => editable && setHover(true)}
        onMouseLeave={() => setHover(false)}
        title={editable ? "Clic para editar el comentario" : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          whiteSpace: "pre-wrap",
          cursor: editable ? "text" : "default",
          borderRadius: 4,
          padding: editable ? "3px 6px" : 0,
          margin: editable ? "-3px -6px" : 0,
          background: hover ? "#eef0f2" : "transparent",
          transition: "background 0.15s",
        }}
      >
        {comment ? (
          <span style={{ flex: 1 }}>{comment}</span>
        ) : (
          <span style={{ color: "#999", fontStyle: "italic", flex: 1 }}>
            {editable ? "Agregar comentario…" : "Sin comentario"}
          </span>
        )}
        {editable && hover && (
          <EditOutlined style={{ color: "#999", fontSize: 12, flexShrink: 0 }} />
        )}
      </div>
    </div>
  );
};

export default InlineComment;
